import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Auth } from 'aws-amplify';
import { resolveTabAccess } from '../auth/pageAccess';
import { friendlyApiError } from '../utils/apiError';
import { useNavigate } from 'react-router-dom';
import AdminNavTabs from '../components/AdminNavTabs';
import { formatMajor } from '../utils/money';
import './AdminDashboard.css';

const currentYear = new Date().getFullYear();
const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';

const defaultTheme = {
  menuColor: '#6236ff',
  menuGradientEnabled: false,
  menuAlignment: 'center',
  menuLocked: true,
  footerLocked: false,
  posHomepageEnabled: true,
  posMenuLanding: 'categories',
  posMenuItemSize: 'large',
  pageBackgroundMode: 'auto',
  pageBackgroundColor: '#f5f3ff',
  backgroundTheme: {
    mode: 'none',
    presetId: '',
    custom: {
      type: 'solid',
      solidColor: '#f5f3ff',
      gradientStart: '#f5f3ff',
      gradientEnd: '#ffffff',
      gradientAngle: 135,
      imageUrl: '',
      imageFit: 'cover',
    },
    overlayColor: '#000000',
    overlayOpacity: 0,
  },
  appFont: 'Space Grotesk',
  heroImageUrl: 'https://francobucket1.s3.amazonaws.com/theme/hero-default.jpg',
  heroImageMode: 'single',
  heroSlideshowImages: [],
  heroSlideshowIntervalMs: 5000,
  heroImageAlt: 'A model showcasing products',
  heroLogoUrl: '',
  heroLogoLightUrl: '',
  heroLogoDarkUrl: '',
  heroLogoAlt: 'Store logo',
  heroTitleText: 'Welcome to EzSellProducts',
  heroTitleColor: '#1f1142',
  subtitleEnabled: true,
  heroSubtitleText: 'Discover quality products for your lifestyle.',
  heroSubtitleColor: '#7a5af5',
  subtitleColorMatchesTitle: false,
  heroButtonColor: '#6236ff',
  heroLayout: 'split',
  heroOverlayTextAlignment: 'left',
  heroOverlayStyle: 'plain',
  productCategories: ['Blankets', 'Bags', 'Accessories'],
  navMenu: {
    products: true,
    home: true,
    shop: true,
    about: true,
    contact: true,
    onlineOrders: false,
  },
  searchBar: {
    enabled: true,
    placeholder: 'Search products...',
  },
  guestSearch: {
    enabled: true,
  },
  specialMessage: {
    enabled: true,
    text: 'Free Shipping for orders over $20',
    style: 'flash',
    color: '#fef3c7',
  },
  footerBusinessName: 'EzSellProducts.com',
  footerCopyright: `© ${currentYear} EzSellProducts.com`,
  footerLinks: {
    privacy: true,
    terms: true,
    facebook: true,
    instagram: true,
  },
  privacyUrl: '/privacy',
  termsUrl: '/terms',
  facebookUrl: 'https://www.facebook.com/ezsellproducts',
  instagramUrl: 'https://www.instagram.com/ezsellproducts',
  loginEnabled: true,
  passkeyEnabled: true,
};

const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';
const BYPASS_ADMIN_AUTH =
  process.env.NODE_ENV === 'development' &&
  String(process.env.REACT_APP_BYPASS_ADMIN_AUTH || '').trim().toLowerCase() === 'true';

const BACKGROUND_THEME_PRESETS = [
  {
    id: 'flora',
    label: 'Flora',
    background:
      "radial-gradient(circle at 18% 22%, rgba(236, 72, 153, 0.18), transparent 56%), radial-gradient(circle at 76% 28%, rgba(34, 197, 94, 0.14), transparent 54%), radial-gradient(circle at 40% 82%, rgba(168, 85, 247, 0.16), transparent 58%), linear-gradient(135deg, #fff1f2 0%, #f5f3ff 60%, #ecfeff 100%)",
  },
  {
    id: 'nature',
    label: 'Nature',
    background:
      "radial-gradient(circle at 22% 18%, rgba(34, 197, 94, 0.20), transparent 60%), radial-gradient(circle at 78% 48%, rgba(132, 204, 22, 0.16), transparent 62%), linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 45%, #f7fee7 100%)",
  },
  {
    id: 'minimal',
    label: 'Minimal',
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
  },
  {
    id: 'sunset',
    label: 'Sunset',
    background:
      'linear-gradient(135deg, rgba(255, 237, 213, 1) 0%, rgba(253, 186, 116, 0.72) 40%, rgba(251, 113, 133, 0.62) 100%)',
  },
  {
    id: 'night',
    label: 'Night',
    background:
      "radial-gradient(circle at 20% 22%, rgba(59, 130, 246, 0.22), transparent 56%), radial-gradient(circle at 72% 40%, rgba(168, 85, 247, 0.18), transparent 58%), linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
  },
];

const ThemeManager = () => {
  const navigate = useNavigate();
  const statusRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [theme, setTheme] = useState(defaultTheme);
  const [generalStoreMode, setGeneralStoreMode] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [categoryInput, setCategoryInput] = useState('');
  const [themeLoading, setThemeLoading] = useState(false);
  const [heroPreview, setHeroPreview] = useState(defaultTheme.heroImageUrl || '');
  const [logoLightPreview, setLogoLightPreview] = useState(defaultTheme.heroLogoLightUrl || '');
  const [logoDarkPreview, setLogoDarkPreview] = useState(defaultTheme.heroLogoDarkUrl || '');
  const [backgroundPreview, setBackgroundPreview] = useState(defaultTheme.backgroundTheme?.custom?.imageUrl || '');
  const [logoLightPreviewLoaded, setLogoLightPreviewLoaded] = useState(false);
  const [logoDarkPreviewLoaded, setLogoDarkPreviewLoaded] = useState(false);
  const [heroPreviewLoaded, setHeroPreviewLoaded] = useState(false);
  const [backgroundPreviewLoaded, setBackgroundPreviewLoaded] = useState(false);
  const [heroSlideUploadsPreview, setHeroSlideUploadsPreview] = useState([]);
  const [previewCatalogAll, setPreviewCatalogAll] = useState([]);
  const [previewCatalogCategory, setPreviewCatalogCategory] = useState([]);
  const [previewCatalogLoading, setPreviewCatalogLoading] = useState(false);
  const [previewCatalogError, setPreviewCatalogError] = useState('');
  const [previewActiveCategory, setPreviewActiveCategory] = useState('');
  const [previewProductsMenuOpen, setPreviewProductsMenuOpen] = useState(false);
  const previewProductsMenuRef = useRef(null);

  const [showScrollTop, setShowScrollTop] = useState(false);

  const [previewHeroSlideIndex, setPreviewHeroSlideIndex] = useState(0);
  const [previewHeroOpacity, setPreviewHeroOpacity] = useState(1);
  const previewHeroTimerRef = useRef(null);
  const previewHeroFadeTimerRef = useRef(null);

  useEffect(() => {
    if (!statusMessage && !error) return;
    try {
      window.setTimeout(() => {
        try {
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
          statusRef.current?.focus?.({ preventScroll: true });
        } catch {
          // ignore
        }
      }, 50);
    } catch {
      // ignore
    }
  }, [statusMessage, error]);

  const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
  const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];

  const handleChange = (field, value) => {
    setTheme((prev) => {
      const next = { ...prev, [field]: value };

      if (field === 'heroTitleColor' && prev.subtitleColorMatchesTitle) {
        next.heroSubtitleColor = value;
      }

      if (field === 'subtitleColorMatchesTitle') {
        next.heroSubtitleColor = value ? prev.heroTitleColor : prev.heroSubtitleColor;
      }

      if (field === 'heroSubtitleColor') {
        next.subtitleColorMatchesTitle = false;
      }

      return next;
    });
  };

  const setBackgroundTheme = (updater) => {
    setTheme((prev) => {
      const existing = prev?.backgroundTheme && typeof prev.backgroundTheme === 'object' ? prev.backgroundTheme : defaultTheme.backgroundTheme;
      const next = typeof updater === 'function' ? updater(existing) : existing;
      return {
        ...prev,
        backgroundTheme: next,
      };
    });
  };

  const setBackgroundThemeCustom = (partial) => {
    setBackgroundTheme((existing) => {
      const prevCustom = existing?.custom && typeof existing.custom === 'object' ? existing.custom : defaultTheme.backgroundTheme.custom;
      return {
        ...existing,
        custom: {
          ...prevCustom,
          ...(partial || {}),
        },
      };
    });
  };

  const clearOnKeyDown = (e, onClear) => {
    if (e.key !== 'Backspace' && e.key !== 'Delete') return;
    e.preventDefault();
    if (typeof onClear === 'function') onClear();
  };

  const handleNavToggle = (item, checked) => {
    setTheme((prev) => ({
      ...prev,
      navMenu: {
        ...(prev.navMenu || defaultTheme.navMenu),
        [item]: checked,
      },
    }));
  };

  const handleSearchConfigChange = (field, value) => {
    setTheme((prev) => ({
      ...prev,
      searchBar: {
        ...(prev.searchBar || defaultTheme.searchBar),
        [field]: field === 'enabled' ? Boolean(value) : value,
      },
    }));
  };

  const handleGuestSearchConfigChange = (field, value) => {
    setTheme((prev) => ({
      ...prev,
      guestSearch: {
        ...(prev.guestSearch || defaultTheme.guestSearch),
        [field]: field === 'enabled' ? Boolean(value) : value,
      },
    }));
  };

  const handleSpecialMessageChange = (field, value) => {
    setTheme((prev) => ({
      ...prev,
      specialMessage: {
        ...(prev.specialMessage || defaultTheme.specialMessage),
        [field]: field === 'enabled' ? Boolean(value) : value,
      },
    }));
  };

  const handleFooterToggle = (field, checked) => {
    setTheme((prev) => ({
      ...prev,
      footerLinks: {
        ...prev.footerLinks,
        [field]: checked,
      },
    }));
  };

  const handleAddCategory = () => {
    const trimmed = categoryInput.trim();
    if (!trimmed) return;
    setTheme((prev) => ({
      ...prev,
      productCategories: [...prev.productCategories, trimmed],
    }));
    setCategoryInput('');
  };

  const handleRemoveCategory = (index) => {
    setTheme((prev) => ({
      ...prev,
      productCategories: prev.productCategories.filter((_, idx) => idx !== index),
    }));
  };

  const applyThemePatch = (incoming = {}) => {
    if (!incoming) return;
    setTheme((prev) => {
      const next = { ...prev, ...incoming };
      next.footerLinks = { ...prev.footerLinks, ...(incoming.footerLinks || {}) };
      next.navMenu = { ...prev.navMenu, ...(incoming.navMenu || {}) };
      next.searchBar = { ...prev.searchBar, ...(incoming.searchBar || {}) };
      next.guestSearch = { ...prev.guestSearch, ...(incoming.guestSearch || {}) };
      next.specialMessage = { ...prev.specialMessage, ...(incoming.specialMessage || {}) };
      return next;
    });
    applyIncomingPreviews(incoming);
  };

  const applyIncomingPreviews = (incoming) => {
    if (!incoming) return;
    if (Object.prototype.hasOwnProperty.call(incoming, 'heroImageUrl')) {
      setHeroPreview(incoming.heroImageUrl || '');
    }
    if (Object.prototype.hasOwnProperty.call(incoming, 'heroLogoLightUrl')) {
      setLogoLightPreview(incoming.heroLogoLightUrl || '');
    }
    if (Object.prototype.hasOwnProperty.call(incoming, 'heroLogoDarkUrl')) {
      setLogoDarkPreview(incoming.heroLogoDarkUrl || '');
    }
    if (Object.prototype.hasOwnProperty.call(incoming, 'backgroundTheme')) {
      const img = incoming?.backgroundTheme?.custom?.imageUrl;
      setBackgroundPreview(typeof img === 'string' ? img : '');
    }
  };

  const loadGeneralConfig = async (token) => {
    if (BYPASS_ADMIN_AUTH) return;
    if (!API_BASE_URL) return;

    try {
      const res = await fetch(`${API_BASE_URL}/admin/general`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      const rawMode = data?.storeMode || data?.store_mode || '';
      setGeneralStoreMode(String(rawMode || '').trim().toUpperCase());
    } catch (generalError) {
      console.error('General settings load error (theme manager):', generalError);
      setGeneralStoreMode('');
    }
  };

  const showPosThemeControls = useMemo(() => {
    if (BYPASS_ADMIN_AUTH) return true;
    const mode = String(generalStoreMode || '').trim().toUpperCase();
    if (!mode) return true;
    return mode === 'POS' || mode === 'HYBRID';
  }, [generalStoreMode]);

  const handleRemoveHeroSlideUrl = (index) => {
    setTheme((prev) => {
      const existing = Array.isArray(prev.heroSlideshowImages) ? prev.heroSlideshowImages : [];
      return {
        ...prev,
        heroSlideshowImages: existing.filter((_, idx) => idx !== index),
      };
    });
  };

  const handleRemoveHeroSlideshowUpload = (index) => {
    setTheme((prev) => {
      const existing = Array.isArray(prev.heroSlideshowUploads) ? prev.heroSlideshowUploads : [];
      return {
        ...prev,
        heroSlideshowUploads: existing.filter((_, idx) => idx !== index),
      };
    });
    setHeroSlideUploadsPreview((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleHeroSlideshowUpload = async (files) => {
    setError('');
    setStatusMessage('');
    const fileList = Array.from(files || []).filter(Boolean);
    if (!fileList.length) return;

    const valid = [];
    for (const file of fileList) {
      if (!file) continue;
      if (file.size > MAX_IMAGE_BYTES) {
        const maxMb = (MAX_IMAGE_BYTES / (1024 * 1024)).toFixed(1);
        setError(`Image is too large. Please choose a file under ${maxMb} MB.`);
        return;
      }
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        setError('Unsupported file type. Please upload PNG, JPG, WEBP, or SVG images.');
        return;
      }
      valid.push(file);
    }

    try {
      const payloads = [];
      const previews = [];
      for (const file of valid) {
        const dataUrl = await readFileAsDataUrl(file);
        payloads.push({
          data: dataUrl,
          filename: file.name,
          mimeType: file.type,
        });
        previews.push(typeof dataUrl === 'string' ? dataUrl : '');
      }

      setTheme((prev) => {
        const existing = Array.isArray(prev.heroSlideshowUploads) ? prev.heroSlideshowUploads : [];
        return {
          ...prev,
          heroSlideshowUploads: [...existing, ...payloads],
        };
      });
      setHeroSlideUploadsPreview((prev) => [...prev, ...previews].filter(Boolean));
    } catch (fileError) {
      console.error('Image read error:', fileError);
      setError('Failed to read selected image. Please try again.');
    }
  };

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (event) => reject(event);
      reader.readAsDataURL(file);
    });

  const buildPngFilename = (originalName) => {
    const raw = String(originalName || '').trim();
    if (!raw) return 'logo.png';
    const lastDot = raw.lastIndexOf('.');
    const base = lastDot > 0 ? raw.slice(0, lastDot) : raw;
    return `${base}.png`;
  };

  const rasterizeFileToPngDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        try {
          let naturalW = Number(img.naturalWidth) || Number(img.width) || 0;
          let naturalH = Number(img.naturalHeight) || Number(img.height) || 0;
          if (!naturalW || !naturalH) {
            naturalW = 512;
            naturalH = 512;
          }

          const maxPx = 512;
          const scale = Math.min(1, maxPx / Math.max(naturalW, naturalH));
          const w = Math.max(1, Math.round(naturalW * scale));
          const h = Math.max(1, Math.round(naturalH * scale));

          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            URL.revokeObjectURL(url);
            reject(new Error('Unable to create canvas context.'));
            return;
          }
          ctx.clearRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          const pngDataUrl = canvas.toDataURL('image/png');
          URL.revokeObjectURL(url);
          resolve(pngDataUrl);
        } catch (err) {
          URL.revokeObjectURL(url);
          reject(err);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Unable to load image for rasterization.'));
      };
      img.src = url;
    });

  // Login-page copy of a logo for the Cognito hosted login page (spec login-branding-sync).
  // Cognito only accepts PNG/JPEG <= 100 KB, so this makes a PNG that fits 700x356, then
  // 350x178 if that is still too big. Resolves null when no copy fits; the upload itself
  // is never blocked by this.
  const LOGIN_LOGO_MAX_BYTES = 100 * 1024;
  const makeLoginLogoDataUrl = (file) =>
    new Promise((resolve) => {
      let url = '';
      try {
        url = URL.createObjectURL(file);
      } catch {
        resolve(null);
        return;
      }
      const img = new Image();
      const finish = (value) => {
        URL.revokeObjectURL(url);
        resolve(value);
      };
      img.onload = () => {
        try {
          const naturalW = Number(img.naturalWidth) || Number(img.width) || 0;
          const naturalH = Number(img.naturalHeight) || Number(img.height) || 0;
          if (!naturalW || !naturalH) {
            finish(null);
            return;
          }
          for (const [boxW, boxH] of [[700, 356], [350, 178]]) {
            const scale = Math.min(1, boxW / naturalW, boxH / naturalH);
            const w = Math.max(1, Math.round(naturalW * scale));
            const h = Math.max(1, Math.round(naturalH * scale));
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              finish(null);
              return;
            }
            ctx.clearRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);
            const dataUrl = canvas.toDataURL('image/png');
            const base64 = String(dataUrl).split(',')[1] || '';
            const bytes = Math.floor((base64.length * 3) / 4);
            if (base64 && bytes <= LOGIN_LOGO_MAX_BYTES) {
              finish(dataUrl);
              return;
            }
          }
          finish(null);
        } catch {
          finish(null);
        }
      };
      img.onerror = () => finish(null);
      img.src = url;
    });

  const handleImageUpload = async (field, file) => {
    setError('');
    setStatusMessage('');

    const imageUploadMap = {
      hero: {
        uploadKey: 'heroImageUpload',
        previewSetter: setHeroPreview,
        fallbackValue: () => theme.heroImageUrl || defaultTheme.heroImageUrl || '',
      },
      background: {
        uploadKey: 'backgroundImageUpload',
        previewSetter: setBackgroundPreview,
        fallbackValue: () => theme?.backgroundTheme?.custom?.imageUrl || defaultTheme?.backgroundTheme?.custom?.imageUrl || '',
      },
      logoLight: {
        uploadKey: 'heroLogoLightUpload',
        stripeUploadKey: 'heroLogoLightStripeUpload',
        loginUploadKey: 'heroLogoLightLoginUpload',
        previewSetter: setLogoLightPreview,
        fallbackValue: () => theme.heroLogoLightUrl || defaultTheme.heroLogoLightUrl || '',
      },
      logoDark: {
        uploadKey: 'heroLogoDarkUpload',
        stripeUploadKey: 'heroLogoDarkStripeUpload',
        loginUploadKey: 'heroLogoDarkLoginUpload',
        previewSetter: setLogoDarkPreview,
        fallbackValue: () => theme.heroLogoDarkUrl || defaultTheme.heroLogoDarkUrl || '',
      },
    };

    const config = imageUploadMap[field];
    if (!config) {
      setError('Unsupported image field.');
      return;
    }

    if (!file) {
      setTheme((prev) => {
        const next = { ...prev };
        delete next[config.uploadKey];
        if (config.stripeUploadKey) delete next[config.stripeUploadKey];
        if (config.loginUploadKey) delete next[config.loginUploadKey];
        return next;
      });
      config.previewSetter(config.fallbackValue());
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      const maxMb = (MAX_IMAGE_BYTES / (1024 * 1024)).toFixed(1);
      setError(`Image is too large. Please choose a file under ${maxMb} MB.`);
      return;
    }

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError('Unsupported file type. Please upload PNG, JPG, WEBP, or SVG images.');
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      const uploadPayload = {
        data: dataUrl,
        filename: file.name,
        mimeType: file.type,
      };

      let stripeUploadPayload = null;
      if (config.stripeUploadKey) {
        const stripeAccepts = ['image/png', 'image/jpeg', 'image/gif'];
        if (stripeAccepts.includes(file.type)) {
          stripeUploadPayload = uploadPayload;
        } else if (file.type === 'image/svg+xml' || file.type === 'image/webp') {
          const pngDataUrl = await rasterizeFileToPngDataUrl(file);
          stripeUploadPayload = {
            data: pngDataUrl,
            filename: buildPngFilename(file.name),
            mimeType: 'image/png',
          };
        }
      }

      let loginUploadPayload = null;
      if (config.loginUploadKey) {
        const loginDataUrl = await makeLoginLogoDataUrl(file);
        if (loginDataUrl) {
          loginUploadPayload = {
            data: loginDataUrl,
            filename: buildPngFilename(file.name),
            mimeType: 'image/png',
          };
        }
      }

      setTheme((prev) => ({
        ...prev,
        [config.uploadKey]: uploadPayload,
        ...(config.stripeUploadKey
          ? { [config.stripeUploadKey]: stripeUploadPayload }
          : {}),
        ...(config.loginUploadKey
          ? { [config.loginUploadKey]: loginUploadPayload }
          : {}),
      }));

      config.previewSetter(typeof dataUrl === 'string' ? dataUrl : '');
    } catch (fileError) {
      console.error('Image read error:', fileError);
      setError('Failed to read selected image. Please try again.');
    }
  };

  const loadThemeConfig = async (token) => {
    if (BYPASS_ADMIN_AUTH) {
      setStatusMessage('Dev auth bypass enabled; using local theme defaults.');
      return;
    }

    if (!API_BASE_URL) {
      setStatusMessage('Theme API is not configured; using defaults.');
      return;
    }

    setThemeLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/theme`, {
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load theme (${response.status})`);
      }

      const data = await response.json();
      if (data?.theme) {
        applyThemePatch(data.theme);
        setStatusMessage('Theme loaded from saved configuration.');
      }
    } catch (themeError) {
      console.error('Theme load error:', themeError);
      setStatusMessage('Using default theme (could not load saved settings).');
    } finally {
      setThemeLoading(false);
    }
  };

  const checkAuth = async () => {
    if (BYPASS_ADMIN_AUTH) {
      setUsername('Dev');
      setTheme(defaultTheme);
      setStatusMessage('Dev auth bypass enabled; using local theme defaults.');
      setLoading(false);
      return;
    }

    try {
      const user = await Auth.currentAuthenticatedUser();
      const session = await Auth.currentSession();
      const access = await resolveTabAccess(session, 'Theme');
      if (!access.allowed) {
        navigate(access.isPosAdmin ? '/admin/channels/pos/queue' : '/admin/login');
        return;
      }

      setUsername(user.username || user.attributes?.email || 'Admin');
      const token = session.getIdToken().getJwtToken();
      setTheme(defaultTheme);
      await Promise.all([loadThemeConfig(token), loadGeneralConfig(token)]);
    } catch (authError) {
      console.error('Auth error (theme manager):', authError);
      navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!logoLightPreview) {
      setLogoLightPreviewLoaded(false);
      return undefined;
    }
    const img = new Image();
    img.onload = () => {
      if (!cancelled) setLogoLightPreviewLoaded(true);
    };
    img.onerror = () => {
      if (!cancelled) setLogoLightPreviewLoaded(false);
    };
    img.src = logoLightPreview;
    return () => {
      cancelled = true;
    };
  }, [logoLightPreview]);

  useEffect(() => {
    let cancelled = false;
    if (!logoDarkPreview) {
      setLogoDarkPreviewLoaded(false);
      return undefined;
    }
    const img = new Image();
    img.onload = () => {
      if (!cancelled) setLogoDarkPreviewLoaded(true);
    };
    img.onerror = () => {
      if (!cancelled) setLogoDarkPreviewLoaded(false);
    };
    img.src = logoDarkPreview;
    return () => {
      cancelled = true;
    };
  }, [logoDarkPreview]);

  useEffect(() => {
    let cancelled = false;
    if (!heroPreview) {
      setHeroPreviewLoaded(false);
      return undefined;
    }
    const img = new Image();
    img.onload = () => {
      if (!cancelled) setHeroPreviewLoaded(true);
    };
    img.onerror = () => {
      if (!cancelled) setHeroPreviewLoaded(false);
    };
    img.src = heroPreview;
    return () => {
      cancelled = true;
    };
  }, [heroPreview]);

  useEffect(() => {
    let cancelled = false;
    if (!backgroundPreview) {
      setBackgroundPreviewLoaded(false);
      return undefined;
    }
    const img = new Image();
    img.onload = () => {
      if (!cancelled) setBackgroundPreviewLoaded(true);
    };
    img.onerror = () => {
      if (!cancelled) setBackgroundPreviewLoaded(false);
    };
    img.src = backgroundPreview;
    return () => {
      cancelled = true;
    };
  }, [backgroundPreview]);

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const normalizeCatalogProduct = (product) => {
    if (!product) return null;
    const priceRaw = product.Price;
    const priceNum = Number(String(priceRaw ?? '').replace(/[^0-9.]/g, ''));
    const priceText = formatMajor(Number.isFinite(priceNum) ? priceNum : 0);

    return {
      id: product.ProductID || String(Math.random()),
      name: product.ProductName || 'Untitled product',
      description: product.Description || '',
      price: priceText,
      category: product.Category || 'Uncategorized',
      image: product.ImageURL || '',
    };
  };

  const fetchPreviewCatalogAll = async () => {
    if (BYPASS_ADMIN_AUTH) return;
    if (!API_BASE_URL) return;
    setPreviewCatalogLoading(true);
    setPreviewCatalogError('');
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const response = await fetch(`${API_BASE_URL}/admin/products`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to load products');
      }
      const data = await response.json();
      setPreviewCatalogAll(Array.isArray(data?.products) ? data.products : []);
    } catch (err) {
      console.error('Preview catalog load error:', err);
      setPreviewCatalogError('Failed to load products');
      setPreviewCatalogAll([]);
    } finally {
      setPreviewCatalogLoading(false);
    }
  };

  const fetchPreviewCatalogCategory = async (category) => {
    if (BYPASS_ADMIN_AUTH) return;
    if (!API_BASE_URL || !category) return;
    setPreviewCatalogLoading(true);
    setPreviewCatalogError('');
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const response = await fetch(
        `${API_BASE_URL}/admin/products?category=${encodeURIComponent(category)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to load products');
      }
      const data = await response.json();
      setPreviewCatalogCategory(Array.isArray(data?.products) ? data.products : []);
    } catch (err) {
      console.error('Preview catalog category error:', err);
      setPreviewCatalogError('Failed to load products');
      setPreviewCatalogCategory([]);
    } finally {
      setPreviewCatalogLoading(false);
    }
  };

  useEffect(() => {
    if (!showPreview) return;
    setPreviewActiveCategory('');
    setPreviewCatalogCategory([]);
    fetchPreviewCatalogAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPreview]);

  const GOOGLE_FONTS = new Set([
    'Space Grotesk',
    'Inter',
    'Poppins',
    'Roboto',
    'Montserrat',
    'Open Sans',
    'Lato',
    'Raleway',
    'Playfair Display',
    'Merriweather',
    'Nunito',
    'Source Sans 3',
  ]);

  const FONT_OPTIONS = [
    'Space Grotesk',
    'Inter',
    'Poppins',
    'Roboto',
    'Montserrat',
    'Open Sans',
    'Lato',
    'Raleway',
    'Playfair Display',
    'Merriweather',
    'Nunito',
    'Source Sans 3',
    'System UI',
    'Arial',
    'Helvetica',
    'Verdana',
    'Tahoma',
    'Trebuchet MS',
    'Georgia',
    'Times New Roman',
    'Garamond',
    'Courier New',
    'Monaco',
    'Impact',
    'Comic Sans MS',
  ];

  const getGoogleFontHref = (fontName) => {
    if (!GOOGLE_FONTS.has(String(fontName || '').trim())) return null;
    const family = encodeURIComponent(String(fontName || '').trim()).replace(/%20/g, '+');
    if (!family) return null;
    return `https://fonts.googleapis.com/css2?family=${family}:wght@400;500;600;700&display=swap`;
  };

  const ensureFontLoaded = (fontName) => {
    const href = getGoogleFontHref(fontName);
    if (!href) return;
    const id = 'theme-manager-font-link';
    const existing = document.getElementById(id);
    if (existing && existing.getAttribute('href') === href) return;
    const link = existing || document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  };

  useEffect(() => {
    ensureFontLoaded(theme.appFont || 'Space Grotesk');
  }, [theme.appFont]);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!previewProductsMenuOpen) return;
      if (
        previewProductsMenuRef.current &&
        !previewProductsMenuRef.current.contains(event.target)
      ) {
        setPreviewProductsMenuOpen(false);
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [previewProductsMenuOpen]);

  useEffect(() => {
    if (!showPreview) {
      setPreviewProductsMenuOpen(false);
    }
  }, [showPreview]);

  const handleLogout = async () => {
    if (BYPASS_ADMIN_AUTH) {
      navigate('/admin/login');
      return;
    }

    try {
      await Auth.signOut();
      navigate('/admin/login');
    } catch (logoutError) {
      console.error('Logout error:', logoutError);
      setError('Failed to log out');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatusMessage('');
    setError('');

    if (BYPASS_ADMIN_AUTH) {
      setError('Dev auth bypass is enabled; saving is disabled.');
      setSaving(false);
      return;
    }

    if (!API_BASE_URL) {
      setError('Theme API base URL is not configured.');
      setSaving(false);
      return;
    }

    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const response = await fetch(`${API_BASE_URL}/admin/theme`, {
        method: 'PUT',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ theme }),
      });

      if (!response.ok) {
        let body = null;
        try { body = JSON.parse(await response.text()); } catch { /* non-JSON */ }
        throw new Error(friendlyApiError(response.status, body, 'Failed to save theme.'));
      }

      const data = await response.json();
      if (data?.theme) {
        applyThemePatch(data.theme);
      }
      setStatusMessage('Theme settings saved.');
    } catch (submitError) {
      console.error('Theme save error:', submitError);
      setError(submitError.message || 'Failed to save theme settings.');
    } finally {
      setSaving(false);
    }
  };

  const previewHeroImageMode = String(theme?.heroImageMode || 'single').toLowerCase();
  const previewHeroImages = useMemo(() => {
    const primary =
      (heroPreview && typeof heroPreview === 'string' && heroPreview.trim() ? heroPreview.trim() : null) ||
      (theme.heroImageUrl && String(theme.heroImageUrl).trim() ? String(theme.heroImageUrl).trim() : null) ||
      'https://francobucket1.s3.amazonaws.com/theme/hero-default.jpg';

    const savedSlides = Array.isArray(theme?.heroSlideshowImages)
      ? theme.heroSlideshowImages.filter((u) => typeof u === 'string' && u.trim()).map((u) => u.trim())
      : [];

    const uploadedSlides = Array.isArray(heroSlideUploadsPreview)
      ? heroSlideUploadsPreview.filter((u) => typeof u === 'string' && u.trim()).map((u) => u.trim())
      : [];

    const merged = [primary, ...savedSlides, ...uploadedSlides];
    const seen = new Set();
    return merged.filter((img) => {
      if (!img) return false;
      if (seen.has(img)) return false;
      seen.add(img);
      return true;
    });
  }, [heroPreview, heroSlideUploadsPreview, theme?.heroImageUrl, theme?.heroSlideshowImages]);

  const previewHeroImageSrc = previewHeroImages[previewHeroSlideIndex] || previewHeroImages[0];

  useEffect(() => {
    setPreviewHeroSlideIndex(0);
    setPreviewHeroOpacity(1);
  }, [showPreview, previewHeroImageMode, previewHeroImages.length]);

  useEffect(() => {
    if (previewHeroTimerRef.current) {
      window.clearInterval(previewHeroTimerRef.current);
      previewHeroTimerRef.current = null;
    }
    if (previewHeroFadeTimerRef.current) {
      window.clearTimeout(previewHeroFadeTimerRef.current);
      previewHeroFadeTimerRef.current = null;
    }

    if (!showPreview) return;
    if (previewHeroImageMode !== 'slideshow') return;
    if (previewHeroImages.length < 2) return;

    let intervalMs = Number.parseInt(theme?.heroSlideshowIntervalMs, 10);
    if (!Number.isFinite(intervalMs)) intervalMs = 5000;
    intervalMs = Math.max(1500, Math.min(60000, intervalMs));

    previewHeroTimerRef.current = window.setInterval(() => {
      setPreviewHeroOpacity(0);
      previewHeroFadeTimerRef.current = window.setTimeout(() => {
        setPreviewHeroSlideIndex((prev) => (prev + 1) % previewHeroImages.length);
        setPreviewHeroOpacity(1);
        previewHeroFadeTimerRef.current = null;
      }, 240);
    }, intervalMs);

    return () => {
      if (previewHeroTimerRef.current) {
        window.clearInterval(previewHeroTimerRef.current);
        previewHeroTimerRef.current = null;
      }
      if (previewHeroFadeTimerRef.current) {
        window.clearTimeout(previewHeroFadeTimerRef.current);
        previewHeroFadeTimerRef.current = null;
      }
    };
  }, [previewHeroImageMode, previewHeroImages, showPreview, theme?.heroSlideshowIntervalMs]);

  if (loading) {
    return (
      <div className="admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading...</p>
      </div>
    );
  }

  const previewFooterLinks = [
    { key: 'privacy', label: 'Privacy Policy' },
    { key: 'terms', label: 'Terms of Use' },
    { key: 'facebook', label: null, icon: 'fab fa-facebook-f' },
    { key: 'instagram', label: null, icon: 'fab fa-instagram' },
  ];

  const heroLayoutOptions = [
    {
      value: 'split',
      label: 'Side by side',
      description: 'Copy column next to a hero photo',
    },
    {
      value: 'overlay',
      label: 'Overlay (blend)',
      description: 'Copy sits on top of the hero image',
    },
  ];

  const heroOverlayStyleOptions = [
    {
      value: 'plain',
      label: 'Plain',
    },
    {
      value: 'scrim_left',
      label: 'Scrim left',
    },
    {
      value: 'glass_card_left',
      label: 'Glass card',
    },
    {
      value: 'centered_chips',
      label: 'Centered + chips',
    },
  ];

  const normalizeHex = (value) => {
    if (typeof value !== 'string') return null;
    const hex = value.trim();
    if (!hex.startsWith('#')) return null;
    const raw = hex.slice(1);
    if (raw.length === 3) {
      return `#${raw
        .split('')
        .map((ch) => `${ch}${ch}`)
        .join('')}`;
    }
    if (raw.length === 6) return `#${raw}`;
    return null;
  };

  const mixHex = (hex, otherHex, amount) => {
    const toRgb = (input) => {
      const normalized = normalizeHex(input);
      if (!normalized) return null;
      const intVal = parseInt(normalized.slice(1), 16);
      return {
        r: (intVal >> 16) & 255,
        g: (intVal >> 8) & 255,
        b: intVal & 255,
      };
    };

    const a = toRgb(hex);
    const b = toRgb(otherHex);
    if (!a || !b) return hex;
    const t = Math.min(1, Math.max(0, amount));
    const r = Math.round(a.r + (b.r - a.r) * t);
    const g = Math.round(a.g + (b.g - a.g) * t);
    const bb = Math.round(a.b + (b.b - a.b) * t);
    return `#${[r, g, bb]
      .map((v) => v.toString(16).padStart(2, '0'))
      .join('')}`;
  };

  const getMenuBackground = (baseColor, gradientEnabled) => {
    if (!gradientEnabled) return baseColor;
    const start = mixHex(baseColor, '#ffffff', 0.18);
    const end = mixHex(baseColor, '#000000', 0.14);
    return `linear-gradient(135deg, ${start} 0%, ${baseColor} 48%, ${end} 100%)`;
  };

  const renderPreview = () => {
    const normalizeHex = (value) => {
      if (typeof value !== 'string') return null;
      const hex = value.trim();
      if (!hex.startsWith('#')) return null;
      const raw = hex.slice(1);
      if (raw.length === 3) {
        return `#${raw
          .split('')
          .map((ch) => `${ch}${ch}`)
          .join('')}`;
      }
      if (raw.length === 6) return hex;
      return null;
    };

    const hexToRgb = (value) => {
      const normalized = normalizeHex(value);
      if (!normalized) return null;
      const intVal = parseInt(normalized.slice(1), 16);
      return {
        r: (intVal >> 16) & 255,
        g: (intVal >> 8) & 255,
        b: intVal & 255,
      };
    };

    const relativeLuminance = (hex) => {
      const rgb = hexToRgb(hex);
      if (!rgb) return 0;
      const srgb = [rgb.r, rgb.g, rgb.b].map((v) => v / 255);
      const linear = srgb.map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
      return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
    };

    const logoBase = theme.heroLogoUrl && theme.heroLogoUrl.trim() ? theme.heroLogoUrl.trim() : null;

    const logoLight =
      (logoLightPreview && typeof logoLightPreview === 'string' && logoLightPreview.trim() ? logoLightPreview.trim() : null) ||
      (theme.heroLogoLightUrl && theme.heroLogoLightUrl.trim() ? theme.heroLogoLightUrl.trim() : null);
    const logoDark =
      (logoDarkPreview && typeof logoDarkPreview === 'string' && logoDarkPreview.trim() ? logoDarkPreview.trim() : null) ||
      (theme.heroLogoDarkUrl && theme.heroLogoDarkUrl.trim() ? theme.heroLogoDarkUrl.trim() : null);

    const menuBaseColor = theme?.menuColor || defaultTheme.menuColor || '#6236ff';
    const menuIsDark = relativeLuminance(menuBaseColor) < 0.5;
    const computedLogo = menuIsDark ? (logoLight || logoDark || logoBase) : (logoDark || logoLight || logoBase);
    const computedHeroImage = previewHeroImageSrc;

    const pageBgMode = String(theme?.pageBackgroundMode || 'auto').toLowerCase();
    const previewPageBackground =
      pageBgMode === 'custom'
        ? theme?.pageBackgroundColor || defaultTheme.pageBackgroundColor || '#f5f3ff'
        : mixHex(menuBaseColor, '#ffffff', 0.75);

    const backgroundTheme = theme?.backgroundTheme && typeof theme.backgroundTheme === 'object' ? theme.backgroundTheme : null;
    const bgMode = String(backgroundTheme?.mode || 'none').toLowerCase();
    const overlayColorRaw = typeof backgroundTheme?.overlayColor === 'string' ? backgroundTheme.overlayColor : '#000000';
    const overlayOpacityRaw = Number(backgroundTheme?.overlayOpacity);
    const overlayOpacity = Number.isFinite(overlayOpacityRaw) ? Math.max(0, Math.min(0.85, overlayOpacityRaw)) : 0;

    const hexToRgba = (hex, alpha) => {
      const normalized = normalizeHex(hex);
      if (!normalized) return `rgba(0,0,0,${alpha})`;
      const intVal = parseInt(normalized.slice(1), 16);
      const r = (intVal >> 16) & 255;
      const g = (intVal >> 8) & 255;
      const b = intVal & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const buildBackgroundLayers = () => {
      if (bgMode === 'none') return null;

      const overlay = overlayOpacity > 0 ? `linear-gradient(${hexToRgba(overlayColorRaw, overlayOpacity)}, ${hexToRgba(overlayColorRaw, overlayOpacity)})` : '';

      if (bgMode === 'preset') {
        const presetId = String(backgroundTheme?.presetId || '').trim().toLowerCase();
        const preset = BACKGROUND_THEME_PRESETS.find((p) => p.id === presetId);
        if (!preset) return overlay || null;
        const base = preset.background;
        return overlay ? `${overlay}, ${base}` : base;
      }

      const custom = backgroundTheme?.custom && typeof backgroundTheme.custom === 'object' ? backgroundTheme.custom : {};
      const type = String(custom.type || 'solid').toLowerCase();
      if (type === 'gradient') {
        const start = typeof custom.gradientStart === 'string' ? custom.gradientStart : '#f5f3ff';
        const end = typeof custom.gradientEnd === 'string' ? custom.gradientEnd : '#ffffff';
        const angle = Number(custom.gradientAngle);
        const a = Number.isFinite(angle) ? Math.max(0, Math.min(360, angle)) : 135;
        const base = `linear-gradient(${a}deg, ${start} 0%, ${end} 100%)`;
        return overlay ? `${overlay}, ${base}` : base;
      }
      if (type === 'image') {
        const url =
          (backgroundPreview && backgroundPreviewLoaded ? backgroundPreview : null) ||
          (typeof custom.imageUrl === 'string' && custom.imageUrl.trim() ? custom.imageUrl.trim() : null);
        if (!url) return overlay || null;
        const base = `url(${url})`;
        return overlay ? `${overlay}, ${base}` : base;
      }

      const color = typeof custom.solidColor === 'string' ? custom.solidColor : '#f5f3ff';
      return overlay ? `${overlay}, linear-gradient(${color}, ${color})` : `linear-gradient(${color}, ${color})`;
    };

    const previewBgLayers = buildBackgroundLayers();
    const previewBgStyle = (() => {
      if (!previewBgLayers) return null;
      const custom = backgroundTheme?.custom && typeof backgroundTheme.custom === 'object' ? backgroundTheme.custom : {};
      const type = String(custom.type || '').toLowerCase();
      const fit = String(custom.imageFit || 'cover').toLowerCase();
      if (bgMode === 'custom' && type === 'image') {
        if (fit === 'tile') {
          return {
            backgroundImage: previewBgLayers,
            backgroundRepeat: 'repeat',
            backgroundSize: 'auto',
            backgroundPosition: 'top left',
          };
        }
        return {
          backgroundImage: previewBgLayers,
          backgroundRepeat: 'no-repeat',
          backgroundSize: fit === 'contain' ? 'contain' : 'cover',
          backgroundPosition: 'center',
        };
      }
      return {
        backgroundImage: previewBgLayers,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    })();

    const footerLinks = [
      { key: 'privacy', label: 'Privacy Policy', url: theme.privacyUrl },
      { key: 'terms', label: 'Terms of Use', url: theme.termsUrl },
      { key: 'facebook', label: 'Facebook', url: theme.facebookUrl },
      { key: 'instagram', label: 'Instagram', url: theme.instagramUrl },
    ].filter((link) => theme.footerLinks[link.key]);

    const previewOverlayStyle = String(theme?.heroOverlayStyle || 'plain').toLowerCase();
    const previewChipCategories = Array.isArray(theme?.productCategories)
      ? theme.productCategories
          .map((c) => String(c || '').trim())
          .filter(Boolean)
          .slice(0, 3)
      : [];

    const overlayGradient = (() => {
      if (!computedHeroImage) {
        return 'linear-gradient(135deg, #a855f7, #7c3aed)';
      }
      if (previewOverlayStyle === 'scrim_left') {
        return `linear-gradient(90deg, rgba(8,8,8,0.82), rgba(8,8,8,0.22) 58%, rgba(8,8,8,0.05)), url(${computedHeroImage})`;
      }
      if (previewOverlayStyle === 'centered_chips') {
        return `linear-gradient(180deg, rgba(8,8,8,0.74), rgba(8,8,8,0.24) 60%, rgba(8,8,8,0.10)), url(${computedHeroImage})`;
      }
      if (previewOverlayStyle === 'glass_card_left') {
        return `linear-gradient(140deg, rgba(8,8,8,0.70), rgba(8,8,8,0.26) 62%, rgba(8,8,8,0.10)), url(${computedHeroImage})`;
      }
      return `linear-gradient(110deg, rgba(8,8,8,0.55), rgba(8,8,8,0)), url(${computedHeroImage})`;
    })();

    const overlayTextStyle = (() => {
      if (previewOverlayStyle !== 'glass_card_left') return undefined;
      return {
        padding: 16,
        borderRadius: 18,
        background: 'rgba(15, 23, 42, 0.24)',
        border: '1px solid rgba(255,255,255,0.18)',
        backdropFilter: 'blur(14px)',
        boxShadow: '0 18px 42px rgba(0,0,0,0.25)',
      };
    })();

    const normalizedAllProducts = Array.isArray(previewCatalogAll)
      ? previewCatalogAll.map(normalizeCatalogProduct).filter(Boolean)
      : [];

    const normalizedCategoryProducts = Array.isArray(previewCatalogCategory)
      ? previewCatalogCategory.map(normalizeCatalogProduct).filter(Boolean)
      : [];

    const availableCategories = Array.from(
      new Set(normalizedAllProducts.map((p) => p.category).filter(Boolean))
    );

    const configuredCategories = Array.isArray(theme.productCategories) ? theme.productCategories : [];
    const availableSet = new Set(availableCategories);
    const menuCategories = configuredCategories.length
      ? configuredCategories.filter((cat) => availableSet.has(cat))
      : availableCategories;

    const allowedSet = configuredCategories.length ? new Set(menuCategories) : null;
    const baseProducts = normalizedAllProducts;
    const categoryProducts = normalizedCategoryProducts.length
      ? normalizedCategoryProducts
      : baseProducts.filter((p) => p.category === previewActiveCategory);

    const productsToShow = (previewActiveCategory ? categoryProducts : baseProducts).filter((p) => {
      if (!p) return false;
      if (previewActiveCategory) return p.category === previewActiveCategory;
      if (allowedSet) return allowedSet.has(p.category);
      return true;
    });

    const handlePreviewShopNow = () => {
      setPreviewActiveCategory('');
      setPreviewCatalogCategory([]);
      setPreviewProductsMenuOpen(false);
    };

    const handlePreviewSelectCategory = (category) => {
      setPreviewActiveCategory(category);
      fetchPreviewCatalogCategory(category);
      setPreviewProductsMenuOpen(false);
    };

    const showProductPage = Boolean(previewActiveCategory);

    const searchConfig = {
      ...(defaultTheme.searchBar || {}),
      ...(theme.searchBar || {}),
    };

    const buildSpecialMessage = () => {
      const config = {
        ...(defaultTheme.specialMessage || {}),
        ...(theme.specialMessage || {}),
      };
      const text = typeof config.text === 'string' ? config.text.trim() : '';
      if (!config.enabled || !text) return null;
      const variant = (config.style || 'flash').toLowerCase() === 'marquee' ? 'marquee' : 'flash';
      const color = config.color && typeof config.color === 'string' ? config.color : defaultTheme.specialMessage.color;

      if (variant === 'marquee') {
        return (
          <div className="preview-special-message marquee" style={{ background: color }} role="status" aria-live="polite">
            <div className="preview-special-message-track">
              <span className="preview-special-message-text marquee-text">{text}</span>
            </div>
          </div>
        );
      }

      return (
        <div className="preview-special-message flash" style={{ background: color }} role="status" aria-live="polite">
          <div className="preview-special-message-inline">
            <i className="fas fa-truck" aria-hidden="true"></i>
            <span className="preview-special-message-text">{text}</span>
          </div>
        </div>
      );
    };

    const renderProductGrid = () => (
      <section className="preview-products">
        {previewCatalogLoading && !productsToShow.length ? (
          <div className="preview-products-status">Loading products…</div>
        ) : previewCatalogError && !productsToShow.length ? (
          <div className="preview-products-status">{previewCatalogError}</div>
        ) : !productsToShow.length ? (
          <div className="preview-products-status">No products found</div>
        ) : (
          <div className="products-grid">
            {productsToShow.map((product) => (
              <div key={product.id} className="product-card">
                <div className="product-image">
                  {product.image ? (
                    <img src={product.image} alt={product.name} />
                  ) : (
                    <div className="no-image">
                      <i className="fas fa-image" aria-hidden="true"></i>
                    </div>
                  )}
                </div>
                <div className="product-details">
                  <h3>{product.name}</h3>
                  <p className="product-description">{product.description}</p>
                  <div className="product-meta">
                    <span className="product-price">{product.price}</span>
                    <button
                      type="button"
                      className="product-category"
                      onClick={() => handlePreviewSelectCategory(product.category)}
                    >
                      {product.category}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    );

    return (
      <div className="preview-card-scale">
        <div
          className={`theme-preview-card${theme.menuLocked ? ' menu-locked' : ''}${theme.footerLocked ? ' footer-locked' : ''}`}
          style={{
            fontFamily: `${theme.appFont || 'Space Grotesk'}, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
            backgroundColor: previewPageBackground,
            '--bg': bgMode !== 'none' ? 'transparent' : previewPageBackground,
            ...(previewBgStyle || {}),
          }}
        >
          <div className="preview-navbar" style={{ background: getMenuBackground(theme.menuColor, theme.menuGradientEnabled) }}>
            <div className="preview-logo">
              {computedLogo ? (
                <img src={computedLogo} alt={theme.heroLogoAlt || 'Brand logo'} />
              ) : (
                <span className="preview-logo-text">
                  {theme.footerBusinessName || defaultTheme.footerBusinessName || 'Your Store'}
                </span>
              )}
            </div>
            <div className={`preview-nav-links align-${theme.menuAlignment || 'center'}`}>
              {theme.navMenu?.home ? (
                <button type="button" className="preview-nav-link ghost" onClick={handlePreviewShopNow}>
                  Home
                </button>
              ) : null}
              {theme.navMenu?.products ? (
                <div
                  ref={previewProductsMenuRef}
                  className={`preview-nav-products ${previewProductsMenuOpen ? 'open' : ''}`}
                >
                  <button
                    type="button"
                    className="preview-nav-link preview-nav-link-button"
                    aria-haspopup="true"
                    aria-expanded={previewProductsMenuOpen}
                    onClick={() => setPreviewProductsMenuOpen((prev) => !prev)}
                  >
                    Products
                    <span className="preview-nav-caret" aria-hidden="true">▾</span>
                  </button>
                  <div className="preview-nav-dropdown">
                    {menuCategories && menuCategories.length > 0 ? (
                      menuCategories.map((category, idx) => (
                        <button
                          key={`${category}-${idx}`}
                          type="button"
                          className="preview-nav-dropdown-item"
                          onClick={() => handlePreviewSelectCategory(category)}
                        >
                          {category}
                        </button>
                      ))
                    ) : (
                      <span className="preview-nav-dropdown-item muted">No categories available</span>
                    )}
                  </div>
                </div>
              ) : null}
              {theme.navMenu?.shop ? (
                <button
                  type="button"
                  className="preview-nav-link ghost"
                  onClick={() => {
                    setPreviewActiveCategory('');
                    setPreviewCatalogCategory([]);
                    setPreviewProductsMenuOpen(false);
                  }}
                >
                  Shop
                </button>
              ) : null}
              {theme.navMenu?.about ? (
                <button type="button" className="preview-nav-link ghost">
                  About
                </button>
              ) : null}
              {theme.navMenu?.contact ? (
                <button type="button" className="preview-nav-link ghost">
                  Contact
                </button>
              ) : null}
            </div>
            <div className="preview-header-actions">
              {searchConfig.enabled ? (
                <form className="preview-search" onSubmit={(event) => event.preventDefault()}>
                  <input
                    type="search"
                    placeholder={searchConfig.placeholder || 'Search products...'}
                    aria-label="Search products"
                  />
                  <button type="submit" aria-label="Submit search">
                    <span aria-hidden="true">⌕</span>
                  </button>
                </form>
              ) : null}
              <button className="preview-login">Login</button>
            </div>
          </div>
          {buildSpecialMessage()}

          {showProductPage ? (
            <div className="preview-product-page">
              <div className="preview-product-page-header">
                <button type="button" className="preview-back-link" onClick={handlePreviewShopNow}>
                  ← Preview home
                </button>
                <div>
                  <p>Category</p>
                  <h2>{previewActiveCategory}</h2>
                </div>
              </div>
              {renderProductGrid()}
            </div>
          ) : (
            <>
              {theme.heroLayout === 'overlay' ? (
                <div
                  className={`preview-hero-overlay align-${theme.heroOverlayTextAlignment || 'left'} overlay-style-${
                    theme.heroOverlayStyle || 'plain'
                  }`}
                  style={{ position: 'relative', overflow: 'hidden' }}
                >
                  <div
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundImage: overlayGradient,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      transition: 'opacity 240ms ease',
                      opacity: previewHeroOpacity,
                    }}
                  />

                  <div
                    className={`preview-overlay-text with-image align-${theme.heroOverlayTextAlignment || 'left'}`}
                    style={{ position: 'relative', zIndex: 1, ...(overlayTextStyle || {}) }}
                  >
                    <h4 style={{ color: '#fff' }}>{theme.heroTitleText || 'Your title'}</h4>
                    {theme.subtitleEnabled ? (
                      <p style={{ color: 'rgba(255,255,255,0.86)' }}>{theme.heroSubtitleText || 'Optional subtitle message'}</p>
                    ) : null}
                    <button
                      type="button"
                      className="preview-cta"
                      style={{ background: theme.heroButtonColor }}
                      onClick={handlePreviewShopNow}
                    >
                      Shop now
                    </button>

                    {previewOverlayStyle === 'centered_chips' && previewChipCategories.length ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                        {previewChipCategories.map((cat) => (
                          <button
                            key={cat}
                            type="button"
                            onClick={() => handlePreviewSelectCategory(cat)}
                            style={{
                              borderRadius: 999,
                              padding: '8px 12px',
                              fontWeight: 700,
                              border: '1px solid rgba(255,255,255,0.18)',
                              background: 'rgba(255,255,255,0.10)',
                              color: '#fff',
                              cursor: 'pointer',
                            }}
                          >
                            {cat}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: 18,
                    padding: '24px clamp(20px, 5vw, 48px) 22px',
                    background: 'var(--bg, #f9faff)',
                    borderTop: '1px solid #edf0fb',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <h4 style={{ color: theme.heroTitleColor, margin: 0 }}>{theme.heroTitleText || 'Your title'}</h4>
                    {theme.subtitleEnabled ? (
                      <p style={{ color: theme.heroSubtitleColor, margin: 0 }}>{theme.heroSubtitleText || 'Optional subtitle message'}</p>
                    ) : null}
                    <button
                      type="button"
                      className="preview-cta"
                      style={{ background: theme.heroButtonColor, width: 'fit-content' }}
                      onClick={handlePreviewShopNow}
                    >
                      Shop now
                    </button>
                  </div>
                  <div
                    style={{
                      borderRadius: 18,
                      minHeight: 180,
                      backgroundImage: computedHeroImage
                        ? `url(${computedHeroImage})`
                        : 'linear-gradient(135deg, #a855f7, #7c3aed)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      transition: 'opacity 240ms ease',
                      opacity: previewHeroOpacity,
                    }}
                  />
                </div>
              )}
              <div className="preview-products-placeholder">
                <div className="placeholder-icon" aria-hidden="true">🛍️</div>
                <h3>Preview your catalog layout</h3>
                <p>
                  Click the <strong>Products</strong> menu above to choose a category and see the live
                  product-only page inside this preview.
                </p>
              </div>
            </>
          )}
          <div className="preview-footer">
            <div className="preview-footer-brand">
              {theme.footerCopyright ||
                `© ${currentYear} ${theme.footerBusinessName || 'Your business'}`}
            </div>
            <div className="preview-footer-links">
              {footerLinks.length === 0 ? (
                <span>No footer links enabled</span>
              ) : (
                footerLinks.map((link) => {
                  const meta = previewFooterLinks.find((item) => item.key === link.key);
                  if (!meta) return null;
                  return (
                    <span key={link.key} className="preview-footer-link-item">
                      {meta.icon ? (
                        <>
                          <i className={meta.icon} aria-hidden="true"></i>
                          <span className="sr-only">{link.label}</span>
                        </>
                      ) : (
                        meta.label
                      )}
                    </span>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-header-logo">
            <i className="fas fa-palette"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <div>
            <h1>Theme Management</h1>
            <p>Brand &amp; appearance settings</p>
          </div>
        </div>
        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield"></i>
            {username}
          </span>
          <button onClick={handleLogout} className="btn-logout">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <div className="admin-content">
        {error && (
          <div className="alert alert-error" ref={statusRef} tabIndex={-1} role="alert" aria-live="assertive">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}
        {themeLoading && (
          <div className="alert alert-info theme-alert">
            <i className="fas fa-spinner fa-spin"></i>
            Loading saved theme...
          </div>
        )}

        <div className="theme-manager-header">
          <div>
            <h2>Theme Settings</h2>
            <p>Control the look &amp; feel of your storefront without touching code.</p>
          </div>
          <button
            type="button"
            className="preview-trigger-button"
            onClick={() => setShowPreview(true)}
          >
            Theme preview
          </button>
        </div>
        {showPreview && (
          <div className="preview-modal">
            <div className="preview-modal-content">
              <button
                type="button"
                className="preview-close-button"
                onClick={() => setShowPreview(false)}
              >
                ×
              </button>
              {renderPreview()}
            </div>
          </div>
        )}

        <form className="theme-form" onSubmit={handleSubmit}>
          <details className="theme-section theme-accordion theme-section--nav">
            <summary className="theme-accordion-summary">
              <span className="theme-accordion-title">
                <i className="fas fa-palette"></i> Navigation, Logo &amp; Background Image
              </span>
              <i className="fas fa-chevron-down theme-accordion-chevron" aria-hidden="true"></i>
            </summary>
            <div className="theme-accordion-body">
              <div className="theme-grid">
              <label className="theme-field">
                <span>Application font</span>
                <select
                  value={theme.appFont || 'Space Grotesk'}
                  onChange={(e) => handleChange('appFont', e.target.value)}
                >
                  {FONT_OPTIONS.map((font) => (
                    <option key={font} value={font}>
                      {font}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="theme-group-panel" style={{ marginTop: 18 }}>
              <div className="theme-group-header">
                <p className="panel-eyebrow">Background</p>
                <h4>
                  <i className="fas fa-fill-drip" aria-hidden="true"></i> Background styling
                </h4>
                <p className="field-hint">Tune the navbar gradient and the overall page background.</p>
              </div>

              <div className="theme-grid two" style={{ marginBottom: 0 }}>
                <label className="theme-field">
                  <span>Theme color</span>
                  <div className="theme-hint">
                    Used for the top navigation background, dropdown menus, and other accent UI styling.
                  </div>
                  <input
                    type="color"
                    value={theme.menuColor}
                    onChange={(e) => handleChange('menuColor', e.target.value)}
                  />
                </label>

                <label className={`nav-toggle-pill ${theme.menuGradientEnabled ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={Boolean(theme.menuGradientEnabled)}
                    onChange={(e) => handleChange('menuGradientEnabled', e.target.checked)}
                  />
                  <span className="icon">
                    <i className="fas fa-brush" aria-hidden="true"></i>
                  </span>
                  <span>{theme.menuGradientEnabled ? 'Gradient theme enabled' : 'Gradient theme disabled'}</span>
                </label>
              </div>

              <div className="theme-grid two" style={{ marginTop: 12, marginBottom: 0 }}>
                <label className={`nav-toggle-pill ${theme.menuLocked ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={Boolean(theme.menuLocked)}
                    onChange={(e) => handleChange('menuLocked', e.target.checked)}
                  />
                  <span className="icon">
                    <i className="fas fa-thumbtack" aria-hidden="true"></i>
                  </span>
                  <span>{theme.menuLocked ? 'Menu locked during scroll' : 'Menu scrolls away'}</span>
                </label>

                <label className={`nav-toggle-pill ${theme.footerLocked ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={Boolean(theme.footerLocked)}
                    onChange={(e) => handleChange('footerLocked', e.target.checked)}
                  />
                  <span className="icon">
                    <i className="fas fa-anchor" aria-hidden="true"></i>
                  </span>
                  <span>{theme.footerLocked ? 'Footer locked during scroll' : 'Footer at page bottom'}</span>
                </label>
              </div>

              <div className="theme-group-panel" style={{ marginTop: 18 }}>
                <div className="theme-group-header">
                  <p className="panel-eyebrow">Auth</p>
                  <h4>
                    <i className="fas fa-user-lock" aria-hidden="true"></i> Login settings
                  </h4>
                  <p className="field-hint">Control which sign-in methods appear in the demo app.</p>
                </div>

                <div className="theme-grid two" style={{ marginBottom: 0 }}>
                  <label className={`nav-toggle-pill ${theme.loginEnabled ? 'active' : ''}`}>
                    <input
                      type="checkbox"
                      checked={Boolean(theme.loginEnabled)}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        handleChange('loginEnabled', enabled);
                        if (!enabled) {
                          handleChange('passkeyEnabled', false);
                        } else {
                          handleChange('passkeyEnabled', true);
                        }
                      }}
                    />
                    <span className="icon">
                      <i className="fas fa-sign-in-alt" aria-hidden="true"></i>
                    </span>
                    <span>{theme.loginEnabled ? 'Login enabled' : 'Login disabled'}</span>
                  </label>

                  <label className="nav-toggle-pill active" style={{ opacity: theme.loginEnabled ? 1 : 0.55 }}>
                    <input type="checkbox" checked disabled />
                    <span className="icon">
                      <i className="fas fa-key" aria-hidden="true"></i>
                    </span>
                    <span>Password (always enabled)</span>
                  </label>
                </div>

                {theme.loginEnabled ? (
                  <div className="theme-grid" style={{ marginTop: 12 }}>
                    <label className={`nav-toggle-pill ${theme.passkeyEnabled ? 'active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={Boolean(theme.passkeyEnabled)}
                        onChange={(e) => handleChange('passkeyEnabled', e.target.checked)}
                      />
                      <span className="icon">
                        <i className="fas fa-fingerprint" aria-hidden="true"></i>
                      </span>
                      <span>{theme.passkeyEnabled ? 'Passkey enabled (optional)' : 'Passkey disabled'}</span>
                    </label>
                  </div>
                ) : null}
              </div>

              <div className="theme-field" style={{ marginTop: 24 }}>
                <span>Page background</span>
                <div className="theme-hint">
                  Choose Auto to generate a super light background from your Theme color, or Custom to pick your own.
                </div>

                <div className="page-background-controls">
                  <div className="segmented-control" role="group" aria-label="Page background mode">
                    <button
                      type="button"
                      className={`segmented-option ${String(theme.pageBackgroundMode || 'auto') === 'auto' ? 'active' : ''}`}
                      onClick={() => handleChange('pageBackgroundMode', 'auto')}
                    >
                      Auto
                    </button>
                    <button
                      type="button"
                      className={`segmented-option ${String(theme.pageBackgroundMode || 'auto') === 'custom' ? 'active' : ''}`}
                      onClick={() => handleChange('pageBackgroundMode', 'custom')}
                    >
                      Custom
                    </button>
                  </div>

                  {String(theme.pageBackgroundMode || 'auto') === 'custom' ? (
                    <div className="page-background-color">
                      <span className="page-background-color-label">Custom background color</span>
                      <input
                        type="color"
                        value={theme.pageBackgroundColor || '#f5f3ff'}
                        onChange={(e) => handleChange('pageBackgroundColor', e.target.value)}
                      />
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="theme-field" style={{ marginTop: 24 }}>
                <span>Background theme</span>
                <div className="theme-hint">Optional full-page background theme (preset or custom). Applies to the entire storefront after saving.</div>

                <div className="page-background-controls" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <div className="segmented-control" role="group" aria-label="Background theme mode">
                    <button
                      type="button"
                      className={`segmented-option ${String(theme?.backgroundTheme?.mode || 'none') === 'none' ? 'active' : ''}`}
                      onClick={() =>
                        setBackgroundTheme((prev) => ({
                          ...(prev || defaultTheme.backgroundTheme),
                          mode: 'none',
                          presetId: '',
                        }))
                      }
                    >
                      None
                    </button>
                    <button
                      type="button"
                      className={`segmented-option ${String(theme?.backgroundTheme?.mode || 'none') === 'preset' ? 'active' : ''}`}
                      onClick={() =>
                        setBackgroundTheme((prev) => ({
                          ...(prev || defaultTheme.backgroundTheme),
                          mode: 'preset',
                          presetId: String(prev?.presetId || 'flora') || 'flora',
                        }))
                      }
                    >
                      Presets
                    </button>
                    <button
                      type="button"
                      className={`segmented-option ${String(theme?.backgroundTheme?.mode || 'none') === 'custom' ? 'active' : ''}`}
                      onClick={() =>
                        setBackgroundTheme((prev) => ({
                          ...(prev || defaultTheme.backgroundTheme),
                          mode: 'custom',
                        }))
                      }
                    >
                      Custom
                    </button>
                  </div>

                  {String(theme?.backgroundTheme?.mode || 'none') === 'preset' ? (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                        {BACKGROUND_THEME_PRESETS.map((preset) => {
                          const active = String(theme?.backgroundTheme?.presetId || '') === preset.id;
                          return (
                            <button
                              key={preset.id}
                              type="button"
                              onClick={() =>
                                setBackgroundTheme((prev) => ({
                                  ...(prev || defaultTheme.backgroundTheme),
                                  mode: 'preset',
                                  presetId: preset.id,
                                }))
                              }
                              style={{
                                textAlign: 'left',
                                borderRadius: 14,
                                border: active ? '2px solid rgba(99, 102, 241, 0.95)' : '1px solid rgba(15, 23, 42, 0.14)',
                                background: 'rgba(255,255,255,0.75)',
                                padding: 12,
                                cursor: 'pointer',
                              }}
                            >
                              <div
                                style={{
                                  height: 72,
                                  borderRadius: 12,
                                  backgroundImage: preset.background,
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center',
                                  border: '1px solid rgba(15, 23, 42, 0.10)',
                                }}
                              />
                              <div style={{ marginTop: 10, fontWeight: 700 }}>{preset.label}</div>
                              <div style={{ marginTop: 4, fontSize: 12, color: 'rgba(15, 23, 42, 0.65)' }}>{active ? 'Selected' : 'Select'}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}

                  {String(theme?.backgroundTheme?.mode || 'none') === 'custom' ? (
                    <div style={{ marginTop: 12 }}>
                      <div className="segmented-control" role="group" aria-label="Custom background type">
                        {['solid', 'gradient', 'image'].map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            className={`segmented-option ${String(theme?.backgroundTheme?.custom?.type || 'solid') === opt ? 'active' : ''}`}
                            onClick={() => setBackgroundThemeCustom({ type: opt })}
                          >
                            {opt === 'solid' ? 'Solid' : opt === 'gradient' ? 'Gradient' : 'Image'}
                          </button>
                        ))}
                      </div>

                      {String(theme?.backgroundTheme?.custom?.type || 'solid') === 'solid' ? (
                        <div className="theme-grid two" style={{ marginTop: 12 }}>
                          <label className="theme-field">
                            <span>Background color</span>
                            <input
                              type="color"
                              value={theme?.backgroundTheme?.custom?.solidColor || '#f5f3ff'}
                              onChange={(e) => setBackgroundThemeCustom({ solidColor: e.target.value })}
                            />
                          </label>
                        </div>
                      ) : null}

                      {String(theme?.backgroundTheme?.custom?.type || 'solid') === 'gradient' ? (
                        <div className="theme-grid two" style={{ marginTop: 12 }}>
                          <label className="theme-field">
                            <span>Gradient start</span>
                            <input
                              type="color"
                              value={theme?.backgroundTheme?.custom?.gradientStart || '#f5f3ff'}
                              onChange={(e) => setBackgroundThemeCustom({ gradientStart: e.target.value })}
                            />
                          </label>
                          <label className="theme-field">
                            <span>Gradient end</span>
                            <input
                              type="color"
                              value={theme?.backgroundTheme?.custom?.gradientEnd || '#ffffff'}
                              onChange={(e) => setBackgroundThemeCustom({ gradientEnd: e.target.value })}
                            />
                          </label>
                          <label className="theme-field" style={{ gridTemplateColumns: '1fr' }}>
                            <span>Gradient angle</span>
                            <input
                              type="number"
                              min="0"
                              max="360"
                              value={Number.isFinite(Number(theme?.backgroundTheme?.custom?.gradientAngle)) ? Number(theme?.backgroundTheme?.custom?.gradientAngle) : 135}
                              style={{ gridColumn: 1 }}
                              onChange={(e) => setBackgroundThemeCustom({ gradientAngle: Number(e.target.value) })}
                            />
                          </label>
                        </div>
                      ) : null}

                      {String(theme?.backgroundTheme?.custom?.type || 'solid') === 'image' ? (
                        <div style={{ marginTop: 12 }}>
                          <div className="theme-grid two" style={{ marginBottom: 0 }}>
                            <div className="theme-field file-field">
                              <span>Background image (upload)</span>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  setBackgroundTheme((prev) => ({
                                    ...(prev || defaultTheme.backgroundTheme),
                                    mode: 'custom',
                                    custom: {
                                      ...((prev && typeof prev.custom === 'object' ? prev.custom : defaultTheme.backgroundTheme.custom) || {}),
                                      type: 'image',
                                    },
                                  }));
                                  handleImageUpload('background', e.target.files?.[0]);
                                }}
                              />
                              {backgroundPreview && backgroundPreviewLoaded && (
                                <div className="theme-image-preview">
                                  <img
                                    src={backgroundPreview}
                                    alt="Background preview"
                                    onError={() => setBackgroundPreviewLoaded(false)}
                                  />
                                </div>
                              )}
                            </div>

                            <div className="theme-field">
                              <span>Image fit</span>
                              <div
                                className="segmented-control"
                                role="group"
                                aria-label="Background image fit"
                                style={{
                                  width: '100%',
                                  maxWidth: '100%',
                                  flexWrap: 'wrap',
                                  justifyContent: 'flex-start',
                                }}
                              >
                                {['cover', 'contain', 'tile'].map((fit) => (
                                  <button
                                    key={fit}
                                    type="button"
                                    className={`segmented-option ${String(theme?.backgroundTheme?.custom?.imageFit || 'cover') === fit ? 'active' : ''}`}
                                    onClick={() => setBackgroundThemeCustom({ imageFit: fit })}
                                  >
                                    {fit}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {String(theme?.backgroundTheme?.mode || 'none') !== 'none' ? (
                    <div className="theme-grid two" style={{ marginTop: 14 }}>
                      <label className="theme-field">
                        <span>Overlay color</span>
                        <input
                          type="color"
                          value={theme?.backgroundTheme?.overlayColor || '#000000'}
                          onChange={(e) =>
                            setBackgroundTheme((prev) => ({
                              ...(prev || defaultTheme.backgroundTheme),
                              overlayColor: e.target.value,
                            }))
                          }
                        />
                      </label>
                      <label className="theme-field">
                        <span>Overlay opacity</span>
                        <input
                          type="range"
                          min="0"
                          max="0.85"
                          step="0.05"
                          value={Number.isFinite(Number(theme?.backgroundTheme?.overlayOpacity)) ? Number(theme?.backgroundTheme?.overlayOpacity) : 0}
                          onChange={(e) =>
                            setBackgroundTheme((prev) => ({
                              ...(prev || defaultTheme.backgroundTheme),
                              overlayOpacity: Number(e.target.value),
                            }))
                          }
                        />
                      </label>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="theme-grid two" style={{ marginTop: 18 }}>
              <div className="theme-field">
                <span>Menu item alignment</span>
                <div className="segmented-control" role="group" aria-label="Menu item alignment">
                  <button
                    type="button"
                    className={`segmented-option ${String(theme.menuAlignment || 'center') === 'left' ? 'active' : ''}`}
                    onClick={() => handleChange('menuAlignment', 'left')}
                  >
                    Left
                  </button>
                  <button
                    type="button"
                    className={`segmented-option ${String(theme.menuAlignment || 'center') === 'center' ? 'active' : ''}`}
                    onClick={() => handleChange('menuAlignment', 'center')}
                  >
                    Center
                  </button>
                  <button
                    type="button"
                    className={`segmented-option ${String(theme.menuAlignment || 'center') === 'right' ? 'active' : ''}`}
                    onClick={() => handleChange('menuAlignment', 'right')}
                  >
                    Right
                  </button>
                </div>
              </div>

              {showPosThemeControls ? (
                <div className="theme-grid" style={{ marginTop: 12, marginBottom: 0 }}>
                  <label className={`nav-toggle-pill ${theme.posHomepageEnabled ? 'active' : ''}`}>
                    <input
                      type="checkbox"
                      checked={Boolean(theme.posHomepageEnabled)}
                      onChange={(e) => handleChange('posHomepageEnabled', e.target.checked)}
                    />
                    <span>{theme.posHomepageEnabled ? 'Show homepage first in POS mode' : 'Go straight to menu in POS mode'}</span>
                  </label>
                </div>
              ) : null}
            </div>
            <div className="theme-group-panel">
              <div className="theme-group-header">
                <p className="panel-eyebrow">Navigation</p>
                <h4>
                  <i className="fas fa-layer-group" aria-hidden="true"></i> Products dropdown
                </h4>
                <p className="field-hint">Add categories (free-form) to populate the Products menu used on your storefront.</p>
              </div>
              <div className="theme-field product-category-field">
                <div className="category-input-row">
                  <input
                    type="text"
                    value={categoryInput}
                    onChange={(e) => setCategoryInput(e.target.value)}
                    placeholder="e.g. Blankets"
                  />
                  <button
                    type="button"
                    className="category-add-button"
                    onClick={handleAddCategory}
                    disabled={!categoryInput.trim()}
                  >
                    Add
                  </button>
                </div>
                <div className="category-chip-group">
                  {theme.productCategories && theme.productCategories.length > 0 ? (
                    theme.productCategories.map((category, idx) => (
                      <span key={`${category}-${idx}`} className="category-chip">
                        {category}
                        <button
                          type="button"
                          aria-label={`Remove ${category}`}
                          onClick={() => handleRemoveCategory(idx)}
                        >
                          ×
                        </button>
                      </span>
                    ))
                  ) : (
                    <span className="field-hint">No categories listed yet.</span>
                  )}
                </div>
              </div>
            </div>

            {showPosThemeControls ? (
              <div className="theme-group-panel">
                <div className="theme-group-header">
                  <p className="panel-eyebrow">POS</p>
                  <h4>
                    <i className="fas fa-utensils" aria-hidden="true"></i> POS menu landing
                  </h4>
                  <p className="field-hint">Choose what customers see first when they open the POS menu (/pos).</p>
                </div>
                <div className="theme-field">
                  <span>Landing view</span>
                  <div className="segmented" style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      className={`segmented-option ${String(theme.posMenuLanding || 'categories') === 'categories' ? 'active' : ''}`}
                      onClick={() => handleChange('posMenuLanding', 'categories')}
                    >
                      Categories
                    </button>
                    <button
                      type="button"
                      className={`segmented-option ${String(theme.posMenuLanding || 'categories') === 'list' ? 'active' : ''}`}
                      onClick={() => handleChange('posMenuLanding', 'list')}
                    >
                      Full menu list
                    </button>
                  </div>
                </div>

                {String(theme.posMenuLanding || 'categories') === 'categories' ? (
                  <div className="theme-field" style={{ marginTop: 14 }}>
                    <span>Category type</span>
                    <div className="segmented" style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        className={`segmented-option ${String(theme.posMenuCategoryMode || 'food') === 'food' ? 'active' : ''}`}
                        onClick={() => handleChange('posMenuCategoryMode', 'food')}
                      >
                        Food category
                      </button>
                      <button
                        type="button"
                        className={`segmented-option ${String(theme.posMenuCategoryMode || 'food') === 'meal_period' ? 'active' : ''}`}
                        onClick={() => handleChange('posMenuCategoryMode', 'meal_period')}
                      >
                        Meal period category
                      </button>
                    </div>
                  </div>
                ) : null}

                <div className="theme-field" style={{ marginTop: 14 }}>
                  <span>Default menu item size</span>
                  <div className="segmented" style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      className={`segmented-option ${String(theme.posMenuItemSize || 'large') === 'large' ? 'active' : ''}`}
                      onClick={() => handleChange('posMenuItemSize', 'large')}
                    >
                      Large
                    </button>
                    <button
                      type="button"
                      className={`segmented-option ${String(theme.posMenuItemSize || 'large') === 'medium' ? 'active' : ''}`}
                      onClick={() => handleChange('posMenuItemSize', 'medium')}
                    >
                      Medium
                    </button>
                    <button
                      type="button"
                      className={`segmented-option ${String(theme.posMenuItemSize || 'large') === 'small' ? 'active' : ''}`}
                      onClick={() => handleChange('posMenuItemSize', 'small')}
                    >
                      Small
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="theme-group-panel">
              <div className="theme-group-header">
                <p className="panel-eyebrow">Brand</p>
                <h4>
                  <i className="fas fa-image" aria-hidden="true"></i> Logo images
                </h4>
                <p className="field-hint">Upload your logo variants (light, dark) and the hero image used on the home page.</p>
              </div>
              <div className="theme-grid two" style={{ marginBottom: 0 }}>
                <div className="theme-field file-field">
                  <span>Logo (light variant)</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('logoLight', e.target.files?.[0])}
                  />
                  {logoLightPreview && logoLightPreviewLoaded && (
                    <div className="theme-image-preview">
                      <img
                        src={logoLightPreview}
                        alt="Light logo preview"
                        onError={() => setLogoLightPreviewLoaded(false)}
                      />
                    </div>
                  )}
                </div>

                <div className="theme-field file-field">
                  <span>Logo (dark variant)</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('logoDark', e.target.files?.[0])}
                  />
                  {logoDarkPreview && logoDarkPreviewLoaded && (
                    <div className="theme-image-preview">
                      <img
                        src={logoDarkPreview}
                        alt="Dark logo preview"
                        onError={() => setLogoDarkPreviewLoaded(false)}
                      />
                    </div>
                  )}
                </div>

                <div className="theme-field file-field">
                  <span>Hero image</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('hero', e.target.files?.[0])}
                  />
                  {heroPreview && heroPreviewLoaded && (
                    <div className="theme-image-preview large">
                      <img src={heroPreview} alt="Hero preview" onError={() => setHeroPreviewLoaded(false)} />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="theme-group-panel">
              <div className="theme-group-header">
                <p className="panel-eyebrow">Hero</p>
                <h4>
                  <i className="fas fa-photo-video" aria-hidden="true"></i> Hero image mode
                </h4>
                <p className="field-hint">Choose between a single hero image or a rotating slideshow.</p>
              </div>
              <div className="theme-grid two" style={{ marginBottom: 0 }}>
                <label className="theme-field">
                  <span>Hero image mode</span>
                  <select
                    className="card-input"
                    value={theme.heroImageMode || 'single'}
                    onChange={(e) => handleChange('heroImageMode', e.target.value)}
                  >
                    <option value="single">Single image</option>
                    <option value="slideshow">Slideshow (auto-rotate)</option>
                  </select>
                </label>

                <label className="theme-field">
                  <span>Slideshow interval (ms)</span>
                  <input
                    type="number"
                    value={theme.heroSlideshowIntervalMs ?? 5000}
                    onChange={(e) => handleChange('heroSlideshowIntervalMs', e.target.value)}
                    min={1500}
                    max={60000}
                    step={500}
                    disabled={String(theme.heroImageMode || 'single') !== 'slideshow'}
                  />
                  {String(theme.heroImageMode || 'single') !== 'slideshow' ? (
                    <div className="theme-hint">Editable only when Hero image mode is set to Slideshow.</div>
                  ) : null}
                </label>
              </div>
            </div>

            {String(theme.heroImageMode || 'single') === 'slideshow' ? (
              <div className="theme-field" style={{ marginTop: 10 }}>
                <span>Additional hero images</span>
                <div className="theme-hint">
                  Upload multiple images for slideshow display. The preview will rotate through the main hero image plus these
                  slides.
                </div>

                <div className="theme-field file-field" style={{ marginTop: 12 }}>
                  <span>Upload slideshow images (multiple)</span>
                  <input type="file" accept="image/*" multiple onChange={(e) => handleHeroSlideshowUpload(e.target.files)} />
                </div>

                {(Array.isArray(theme.heroSlideshowImages) && theme.heroSlideshowImages.length > 0) ||
                heroSlideUploadsPreview.length > 0 ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(84px, 1fr))',
                      gap: 12,
                      marginTop: 12,
                    }}
                  >
                    {(Array.isArray(theme.heroSlideshowImages) ? theme.heroSlideshowImages : []).map((url, idx) => (
                      <div
                        key={`${url}-${idx}`}
                        style={{
                          position: 'relative',
                          borderRadius: 12,
                          overflow: 'hidden',
                          border: '1px solid rgba(15, 23, 42, 0.14)',
                          background: 'rgba(255,255,255,0.7)',
                        }}
                      >
                        <img
                          src={url}
                          alt={`Hero slide ${idx + 1}`}
                          style={{ width: '100%', height: 84, objectFit: 'cover', display: 'block' }}
                        />
                        <button
                          type="button"
                          aria-label={`Remove hero slide ${idx + 1}`}
                          onClick={() => handleRemoveHeroSlideUrl(idx)}
                          style={{
                            position: 'absolute',
                            top: 6,
                            right: 6,
                            width: 26,
                            height: 26,
                            borderRadius: 999,
                            background: 'rgba(15, 23, 42, 0.78)',
                            color: '#fff',
                            fontWeight: 800,
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}

                    {heroSlideUploadsPreview.map((url, idx) => (
                      <div
                        key={`${url}-${idx}`}
                        style={{
                          position: 'relative',
                          borderRadius: 12,
                          overflow: 'hidden',
                          border: '1px solid rgba(15, 23, 42, 0.14)',
                          background: 'rgba(255,255,255,0.7)',
                        }}
                      >
                        <img
                          src={url}
                          alt={`New hero slide ${idx + 1}`}
                          style={{ width: '100%', height: 84, objectFit: 'cover', display: 'block' }}
                        />
                        <button
                          type="button"
                          aria-label={`Remove uploaded hero slide ${idx + 1}`}
                          onClick={() => handleRemoveHeroSlideshowUpload(idx)}
                          style={{
                            position: 'absolute',
                            top: 6,
                            right: 6,
                            width: 26,
                            height: 26,
                            borderRadius: 999,
                            background: 'rgba(15, 23, 42, 0.78)',
                            color: '#fff',
                            fontWeight: 800,
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="field-hint" style={{ display: 'inline-block', marginTop: 10 }}>
                    No additional slides yet.
                  </span>
                )}
              </div>
            ) : null}
            <div className="navigation-control-grid">
              <div className="nav-visibility-panel">
                <div className="nav-panel-header">
                  <div>
                    <p className="panel-eyebrow">Navigation</p>
                    <h4>
                      <i className="fas fa-grip-lines"></i> Visible menu links
                    </h4>
                    <p className="field-hint">Toggle the destinations shoppers see in the storefront header.</p>
                  </div>
                </div>
                <div className="nav-toggle-grid">
                  {[
                    { key: 'home', label: 'Home', icon: 'fas fa-home' },
                    { key: 'onlineOrders', label: 'Online Orders', icon: 'fas fa-bag-shopping' },
                    { key: 'products', label: 'Products dropdown', icon: 'fas fa-layer-group' },
                    { key: 'shop', label: 'Shop landing page', icon: 'fas fa-store' },
                    { key: 'about', label: 'About anchor', icon: 'fas fa-heart' },
                    { key: 'contact', label: 'Contact anchor', icon: 'fas fa-envelope-open' },
                  ].map((link) => {
                    const checked = Boolean(theme.navMenu?.[link.key]);
                    return (
                      <label
                        key={link.key}
                        className={`nav-toggle-pill ${checked ? 'active' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => handleNavToggle(link.key, e.target.checked)}
                        />
                        <span className="icon">
                          <i className={link.icon} aria-hidden="true"></i>
                        </span>
                        <span>{link.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="nav-feature-stack">
                <div className="feature-card search-card">
                  <div className="card-header">
                    <p className="panel-eyebrow">Search</p>
                    <h4>Catalog search bar</h4>
                    <p className="field-hint">Optional quick-find entry above the cart button.</p>
                  </div>
                  <div className="card-controls">
                    <label className="toggle-chip">
                      <input
                        type="checkbox"
                        checked={Boolean(theme.searchBar?.enabled)}
                        onChange={(e) => handleSearchConfigChange('enabled', e.target.checked)}
                      />
                      <span>{theme.searchBar?.enabled ? 'Visible' : 'Hidden'}</span>
                    </label>
                    <input
                      type="text"
                      className="card-input"
                      value={theme.searchBar?.placeholder || ''}
                      onChange={(e) => handleSearchConfigChange('placeholder', e.target.value)}
                      placeholder="Search products..."
                      disabled={!theme.searchBar?.enabled}
                    />
                  </div>
                </div>
                <div className="feature-card search-card">
                  <div className="card-header">
                    <p className="panel-eyebrow">Search</p>
                    <h4>Guest search</h4>
                    <p className="field-hint">Allow guests to track orders by order number.</p>
                  </div>
                  <div className="card-controls">
                    <label className="toggle-chip">
                      <input
                        type="checkbox"
                        checked={Boolean(theme.guestSearch?.enabled)}
                        onChange={(e) => handleGuestSearchConfigChange('enabled', e.target.checked)}
                      />
                      <span>{theme.guestSearch?.enabled ? 'Visible' : 'Hidden'}</span>
                    </label>
                  </div>
                </div>
                <div className="feature-card message-card">
                  <div className="card-header">
                    <p className="panel-eyebrow">Announcement</p>
                    <h4>Special banner</h4>
                    <p className="field-hint">Highlight promos like shipping perks or seasonal drops.</p>
                  </div>
                  <label className="toggle-chip">
                    <input
                      type="checkbox"
                      checked={Boolean(theme.specialMessage?.enabled)}
                      onChange={(e) => handleSpecialMessageChange('enabled', e.target.checked)}
                    />
                    <span>{theme.specialMessage?.enabled ? 'Showing' : 'Hidden'}</span>
                  </label>
                  {theme.specialMessage?.enabled && (
                    <div className="card-controls stacked">
                      <input
                        type="text"
                        className="card-input"
                        value={theme.specialMessage?.text || ''}
                        onChange={(e) => handleSpecialMessageChange('text', e.target.value)}
                        placeholder="Free Shipping for orders over $20"
                      />
                      <select
                        className="card-input"
                        value={theme.specialMessage?.style || 'flash'}
                        onChange={(e) => handleSpecialMessageChange('style', e.target.value)}
                      >
                        <option value="flash">Flash (static)</option>
                        <option value="marquee">Marquee (scrolling)</option>
                      </select>
                      <label className="theme-field banner-color-field">
                        <span>Banner color</span>
                        <input
                          type="color"
                          value={theme.specialMessage?.color || '#fef3c7'}
                          onChange={(e) => handleSpecialMessageChange('color', e.target.value)}
                        />
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          </details>

          <details className="theme-section theme-accordion theme-section--hero">
            <summary className="theme-accordion-summary">
              <span className="theme-accordion-title">
                <i className="fas fa-heading"></i> Hero Content
              </span>
              <i className="fas fa-chevron-down theme-accordion-chevron" aria-hidden="true"></i>
            </summary>
            <div className="theme-accordion-body">
              <div className="theme-group-panel">
                <div className="theme-group-header">
                  <p className="panel-eyebrow">Hero</p>
                  <h4>
                    <i className="fas fa-heading" aria-hidden="true"></i> Title &amp; subtitle
                  </h4>
                  <p className="field-hint">Configure your hero headline, subtitle, and button color.</p>
                </div>

                <div className="theme-grid two" style={{ marginBottom: 0 }}>
                  <label className="theme-field hero-span-two">
                    <span>Main title text</span>
                    <input
                      type="text"
                      value={theme.heroTitleText}
                      onChange={(e) => handleChange('heroTitleText', e.target.value)}
                      placeholder="Welcome message"
                    />
                  </label>

                  <label className="theme-field">
                    <span>Hero title color</span>
                    <input
                      type="color"
                      value={theme.heroTitleColor}
                      onChange={(e) => handleChange('heroTitleColor', e.target.value)}
                    />
                  </label>

                  <label className="theme-field">
                    <span>Hero button color</span>
                    <input
                      type="color"
                      value={theme.heroButtonColor}
                      onChange={(e) => handleChange('heroButtonColor', e.target.value)}
                    />
                  </label>

                  <label className={`nav-toggle-pill hero-span-two ${theme.subtitleEnabled ? 'active' : ''}`}>
                    <input
                      type="checkbox"
                      checked={theme.subtitleEnabled}
                      onChange={(e) => handleChange('subtitleEnabled', e.target.checked)}
                    />
                    <span className="icon">
                      <i className="fas fa-closed-captioning" aria-hidden="true"></i>
                    </span>
                    <span>{theme.subtitleEnabled ? 'Subtitle enabled' : 'Subtitle disabled'}</span>
                  </label>

                  {theme.subtitleEnabled && (
                    <>
                      <label className="theme-field hero-span-two">
                        <span>Subtitle text</span>
                        <input
                          type="text"
                          value={theme.heroSubtitleText}
                          onChange={(e) => handleChange('heroSubtitleText', e.target.value)}
                          placeholder="Embrace the warmth..."
                        />
                      </label>
                      <div className="theme-field subtitle-color-field hero-span-two">
                        <span>Subtitle color</span>
                        <div className="subtitle-controls">
                          <input
                            type="color"
                            value={theme.heroSubtitleColor}
                            onChange={(e) => handleChange('heroSubtitleColor', e.target.value)}
                            disabled={theme.subtitleColorMatchesTitle}
                          />
                          <label className={`nav-toggle-pill ${theme.subtitleColorMatchesTitle ? 'active' : ''}`}>
                            <input
                              type="checkbox"
                              checked={theme.subtitleColorMatchesTitle}
                              onChange={(e) => handleChange('subtitleColorMatchesTitle', e.target.checked)}
                            />
                            <span className="icon">
                              <i className="fas fa-equals" aria-hidden="true"></i>
                            </span>
                            <span>Same as title color</span>
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="theme-group-panel" style={{ marginTop: 18 }}>
                <div className="theme-group-header">
                  <p className="panel-eyebrow">Hero</p>
                  <h4>
                    <i className="fas fa-columns" aria-hidden="true"></i> Layout
                  </h4>
                  <p className="field-hint">Choose layout and overlay text alignment.</p>
                </div>

                <div className="layout-toggle" style={{ marginTop: 0 }}>
                <span>Hero layout</span>
                <div className="layout-options">
                  {heroLayoutOptions.map((option) => (
                    <button
                      type="button"
                      key={option.value}
                      className={`layout-option ${theme.heroLayout === option.value ? 'active' : ''}`}
                      onClick={() => handleChange('heroLayout', option.value)}
                    >
                      <span className="layout-option-label">{option.label}</span>
                      <span className="layout-option-desc">{option.description}</span>
                    </button>
                  ))}
                </div>
                </div>

                <div className="layout-toggle">
                <span>Overlay text alignment</span>
                <div className="theme-hint">Only applies when Hero layout is set to Overlay.</div>
                <div
                  className={`segmented-control ${theme.heroLayout !== 'overlay' ? 'disabled' : ''}`}
                  role="group"
                  aria-label="Hero overlay text alignment"
                >
                  <button
                    type="button"
                    className={`segmented-option ${
                      String(theme.heroOverlayTextAlignment || 'left') === 'left' ? 'active' : ''
                    }`}
                    onClick={() => handleChange('heroOverlayTextAlignment', 'left')}
                    disabled={theme.heroLayout !== 'overlay'}
                  >
                    Left
                  </button>
                  <button
                    type="button"
                    className={`segmented-option ${
                      String(theme.heroOverlayTextAlignment || 'left') === 'center' ? 'active' : ''
                    }`}
                    onClick={() => handleChange('heroOverlayTextAlignment', 'center')}
                    disabled={theme.heroLayout !== 'overlay'}
                  >
                    Center
                  </button>
                  <button
                    type="button"
                    className={`segmented-option ${
                      String(theme.heroOverlayTextAlignment || 'left') === 'right' ? 'active' : ''
                    }`}
                    onClick={() => handleChange('heroOverlayTextAlignment', 'right')}
                    disabled={theme.heroLayout !== 'overlay'}
                  >
                    Right
                  </button>
                </div>
                </div>

                <div className="layout-toggle">
                <span>Overlay style</span>
                <div className="theme-hint">Only applies when Hero layout is set to Overlay.</div>
                <div
                  className={`segmented-control ${theme.heroLayout !== 'overlay' ? 'disabled' : ''}`}
                  role="group"
                  aria-label="Hero overlay style"
                >
                  {heroOverlayStyleOptions.map((option) => (
                    <button
                      type="button"
                      key={option.value}
                      className={`segmented-option ${
                        String(theme.heroOverlayStyle || 'plain') === option.value ? 'active' : ''
                      }`}
                      onClick={() => handleChange('heroOverlayStyle', option.value)}
                      disabled={theme.heroLayout !== 'overlay'}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                </div>
              </div>
            </div>
          </details>

          <details className="theme-section theme-accordion theme-section--footer">
            <summary className="theme-accordion-summary">
              <span className="theme-accordion-title">
                <i className="fas fa-link"></i> Footer &amp; Links
              </span>
              <i className="fas fa-chevron-down theme-accordion-chevron" aria-hidden="true"></i>
            </summary>
            <div className="theme-accordion-body">
              <div className="theme-group-panel">
                <div className="theme-group-header">
                  <p className="panel-eyebrow">Footer</p>
                  <h4>
                    <i className="fas fa-copyright" aria-hidden="true"></i> Footer text
                  </h4>
                  <p className="field-hint">Used in the storefront footer and as placeholders in legal pages.</p>
                </div>

                <div className="footer-info-grid" style={{ marginBottom: 0 }}>
                  <label className="theme-field">
                    <span>Business name</span>
                    <input
                      type="text"
                      value={theme.footerBusinessName}
                      onChange={(e) => handleChange('footerBusinessName', e.target.value)}
                      placeholder="Used inside Privacy & Terms copy"
                    />
                  </label>
                  <label className="theme-field">
                    <span>Copyright text</span>
                    <input
                      type="text"
                      value={theme.footerCopyright}
                      onChange={(e) => handleChange('footerCopyright', e.target.value)}
                      placeholder={` ${currentYear} ${theme.footerBusinessName}`}
                    />
                  </label>
                  <div className="theme-field footer-help">
                    <p>
                      This information becomes the dynamic footer + legal placeholders for each tenant,
                      so keep it short and brand specific.
                    </p>
                  </div>
                </div>
              </div>

              <div className="theme-group-panel" style={{ marginTop: 18 }}>
                <div className="theme-group-header">
                  <p className="panel-eyebrow">Footer</p>
                  <h4>
                    <i className="fas fa-link" aria-hidden="true"></i> Footer links
                  </h4>
                  <p className="field-hint">Enable links and set their URLs.</p>
                </div>

                <div className="theme-links-grid" style={{ marginTop: 0 }}>
                  {['privacy', 'terms', 'facebook', 'instagram'].map((linkKey) => (
                    <div key={linkKey} className="theme-link-card">
                      <label className={`nav-toggle-pill ${theme.footerLinks[linkKey] ? 'active' : ''}`}>
                        <input
                          type="checkbox"
                          checked={theme.footerLinks[linkKey]}
                          onChange={(e) => handleFooterToggle(linkKey, e.target.checked)}
                        />
                        <span className="icon">
                          <i
                            className={
                              linkKey === 'privacy'
                                ? 'fas fa-user-shield'
                                : linkKey === 'terms'
                                  ? 'fas fa-file-contract'
                                  : linkKey === 'facebook'
                                    ? 'fab fa-facebook-f'
                                    : 'fab fa-instagram'
                            }
                            aria-hidden="true"
                          ></i>
                        </span>
                        <span>
                          {linkKey === 'privacy'
                            ? 'Privacy policy'
                            : linkKey === 'terms'
                              ? 'Terms of use'
                              : linkKey === 'facebook'
                                ? 'Facebook link'
                                : 'Instagram link'}
                        </span>
                      </label>

                      <input
                        type="text"
                        disabled={!theme.footerLinks[linkKey]}
                        value={
                          linkKey === 'privacy'
                            ? theme.privacyUrl
                            : linkKey === 'terms'
                              ? theme.termsUrl
                              : linkKey === 'facebook'
                                ? theme.facebookUrl
                                : theme.instagramUrl
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          if (linkKey === 'privacy') handleChange('privacyUrl', value);
                          if (linkKey === 'terms') handleChange('termsUrl', value);
                          if (linkKey === 'facebook') handleChange('facebookUrl', value);
                          if (linkKey === 'instagram') handleChange('instagramUrl', value);
                        }}
                        placeholder={linkKey === 'privacy' || linkKey === 'terms' ? '/privacy' : 'https://'}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </details>

          {statusMessage && (
            <div className="alert alert-success theme-alert" ref={statusRef} tabIndex={-1} role="status" aria-live="polite">
              <i className="fas fa-info-circle"></i>
              {statusMessage}
            </div>
          )}

          <div className="theme-actions">
            <button type="submit" className="btn-primary" disabled={saving || themeLoading}>
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  Save theme settings
                </>
              )}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setTheme(defaultTheme);
                setLogoLightPreview(null);
                setLogoDarkPreview(null);
                setHeroPreview(null);
                setStatusMessage('Reverted to default branding.');
              }}
            >
              <i className="fas fa-undo"></i>
              Reset to defaults
            </button>
          </div>
        </form>
      </div>

      <button
        type="button"
        className={`admin-scroll-top ${showScrollTop ? '' : 'hidden'}`}
        onClick={handleScrollTop}
        aria-label="Scroll to top"
        title="Scroll to top"
        style={{ backgroundColor: theme?.menuColor || undefined }}
      >
        <i className="fas fa-arrow-up"></i>
      </button>
    </div>
  );
};

export default ThemeManager;
